<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />

    <!-- ProjectsHeader -->
    <CentralHeader
      :title="'Projects'"
      :count="isLoading ? null : projects?.length"
      @search="filterProjects"
      :routeName="'CreateProjectPage'"
    />

    <!-- Main Content -->
    <div class="flex-1 flex flex-col mb-14 overflow-y-auto">
      <div
        v-if="isLoading"
        class="text-center flex-1 flex justify-center items-center m-auto h-full"
      >
        Loading projects...
      </div>
      <div
        v-else-if="errorMessage"
        class="text-center flex-1 flex justify-center items-center m-auto h-full text-red-500"
      >
        {{ errorMessage }}
      </div>
      <div
        v-else-if="Array.isArray(filteredProjects) && filteredProjects.length === 0"
        class="text-center flex-1 flex justify-center gap-3 items-center m-auto h-full text-total-white-500"
      >
        <div v-if="isSearchMode" class="text-center">No projects found.</div>
        <button
          v-else
          @click="goToCreateProject"
          class="text-prime-golden flex items-center gap-3"
        >
          <img src="../../assets/images/add-icon.svg" class="w-6 h-6" /> Click to add a
          project!
        </button>
      </div>
      <div v-else>
        <div v-for="(project, index) in filteredProjects" :key="project.id">
          <ProjectCard :project="project" :arrayIndex="index" />
        </div>
      </div>
    </div>

    <!-- Pagination Controls (Fixed at the bottom) -->
    <div
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <button
        @click="previousPage"
        :disabled="currentPage == 1"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-left"></i>
      </button>

      <span class="text-sm text-total-white mx-4">
        <!-- Show total pages for search, otherwise just current page -->
        <template v-if="isSearchMode"> {{ currentPage }}/{{ totalPages }} </template>
        <template v-else> {{ currentPage }} </template>
      </span>

      <button
        @click="nextPage"
        :disabled="!hasMoreProjects"
        class="text-prime-golden px-2 disabled:cursor-not-allowed disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import CentralHeader from "@/components/CentralHeader.vue";
import ProjectCard from "@/components/ProjectCard.vue";
import { useProjectStore } from "@/stores/project";
import { useUserStore } from "@/stores/user";
import { useRouter } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import { useLogStore } from "@/stores/log";
import { useAuthStore } from "@/stores/auth";

// Initialize stores
const projectStore = useProjectStore();
const userStore = useUserStore();
const authStore = useAuthStore();
// Reactive variables
const filteredProjects = ref([]);
const isLoading = ref(false);
const errorMessage = ref("");
const hasMoreProjects = ref(true); // Track if there are more projects to load

// Pagination Variables
const currentPage = ref(1);
const itemsPerPage = ref(10); // Change this value to modify items per page
const isSearchMode = ref(false); // Track whether we are in search mode

const router = useRouter();
const logStore = useLogStore();

onMounted(async () => {
  try {
    const data = await projectStore.fetchInterimProject();
    if (data && data.interim_project_id) {
      console.log(data, "data");
      // Force the user to complete project creation (starting at step 3)
      router.push({ name: "CreateProjectPage", query: { step: 3 } });
    }
  } catch (error) {
    console.log("No interim project in progress");
  }
});

onMounted(async () => {
  await logStore.fetchProjectsWithNewActivity();

  if (!authStore.verificationAndAuthorizationValuesExist) {
    await userStore.fetchUserProfile();
    location.reload(); // Refresh the page after fetching user profile
  }

  if (!authStore.isEmailVerified) {
    //console.log('Email not verified');
    //router.push({ name: 'VerifyEmail' });
  }

  if (!authStore.isPhoneVerified) {
    //console.log('Phone not verified');
    router.push({ name: "VerifyPhone" });
  }

  if (!authStore.isEmailAuthorized) {
    //console.log('Email not authorized');
    router.push({ name: "LinkEmail" });
  }
});

// Computed properties
const projects = computed(() =>
  Array.isArray(projectStore.projects) ? projectStore.projects : []
);

// Calculate total pages based on items per page (used for search)
const totalPages = computed(() => {
  return Math.ceil(filteredProjects.value.length / itemsPerPage.value);
});

const goToCreateProject = () => {
  router.push({ name: "CreateProjectPage" });
};
// Methods

// Fetch all projects without assuming a total count
const fetchAllProjects = async (page = 1) => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const size = itemsPerPage.value;
    await projectStore.fetchAllProjects(page, size); // Fetch the current page of projects

    // If fewer than expected projects were returned, block next page
    hasMoreProjects.value = projectStore.projects.length === size;
    filteredProjects.value = projectStore.projects; // Update filteredProjects with the new projects

    await projectStore.fetchAllProjects(page, itemsPerPage.value);

    // Set `hasNewActivity` for each project based on `newActivityProjects`
    const newActivityProjectIds = Array.isArray(logStore.newActivityProjects)
      ? logStore.newActivityProjects.map((projectId) => projectId)
      : [];
    filteredProjects.value = projectStore.projects.map((project) => ({
      ...project,
      hasNewActivity: newActivityProjectIds.includes(project.id),
    }));

    hasMoreProjects.value = projectStore.projects.length === itemsPerPage.value;
  } catch (error) {
    errorMessage.value = "Failed to load projects.";
    console.error("Error fetching projects:", error);
    hasMoreProjects.value = false;
  } finally {
    isLoading.value = false;
  }
};

// Search projects with total pages logic
const filterProjects = async (query) => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    isSearchMode.value = !!query;
    currentPage.value = 1; // Reset to first page on search
    if (!query) {
      // If query is empty, fetch all projects
      await fetchAllProjects(currentPage.value);
    } else {
      // Call the searchProjects action with the query
      await projectStore.searchProjects(query, currentPage.value, itemsPerPage.value);
      filteredProjects.value = Array.isArray(projects.value) ? projects.value : [];

      const newActivityProjectIds = Array.isArray(logStore.newActivityProjects)
        ? logStore.newActivityProjects.map((projectId) => projectId)
        : [];
      filteredProjects.value = projectStore.projects.map((project) => ({
        ...project,
        hasNewActivity: newActivityProjectIds.includes(project.id),
      }));
      hasMoreProjects.value = currentPage.value < totalPages.value;
    }
  } catch (error) {
    errorMessage.value = "Failed to load projects.";
    console.error("Error searching projects:", error);
    filteredProjects.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Fetch projects on component mount (initial load)
onMounted(async () => {
  await fetchAllProjects(currentPage.value);
});

// Fetch user profile if needed
onMounted(async () => {
  if (!userStore.profile) {
    try {
      await userStore.fetchUserProfile();
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  }
});

// Pagination Methods
const nextPage = async () => {
  if (!isSearchMode.value) {
    currentPage.value += 1;
    await fetchAllProjects(currentPage.value);
  } else if (isSearchMode.value && currentPage.value < totalPages.value) {
    currentPage.value += 1;
    await filterProjects(searchQuery.value); // Make sure to pass the current search query
  }
};

const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    if (!isSearchMode.value) {
      await fetchAllProjects(currentPage.value);
    } else {
      await filterProjects(searchQuery.value);
    }
  }
};

// Store the current search query
const searchQuery = ref("");

// Update searchQuery when filterProjects is called
watch(searchQuery, (newQuery) => {
  filterProjects(newQuery);
});
</script>

<style scoped>
/* Add any additional styles here */
</style>
